import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';

import { MultiSelect } from '~components/form-fields/inputs';
import { TableView } from '~components/table-view';

import budgetColumnsFor from '~support-at-home/forms/sah-associations/cutoverBudgets';

import { getAll as getBudgetStatuses } from '~support-at-home/actions/clientBudgetStatuses';
import { CutoverContext, useGetCutoverBudgetsQuery } from '~support-at-home/actions/sahServiceCutover';

import { selectClientBudgetStatuses } from '~support-at-home/selectors/clientBudgetStatuses';

import { usePublishedValue } from './hooks/useSelectionSync';
import StepPanel from './StepPanel';

/** Budgets per page. Matches the server's own default so the first page needs no extra argument. */
const PAGE_SIZE = 20;

interface ComponentProps {
  value: CutoverContext & {
    service_type_ids?: number[];
    status_ids?: string[];
    excluded_budget_ids?: string[];
  };
  onChange?: (name: string, value: any) => any;
}

/**
 * Step 4 - the client budgets holding a line for one of these services past the cutover.
 *
 * Confirming ends each of those lines the day before the cutover and gives the dates they give up to
 * a line for the replacement, in the section for the replacement's contribution category. That is
 * what makes the participant contribution right on both sides: the earlier portion keeps the
 * treatment that applied then, and the later portion is treated under the new category.
 *
 * **Selection is by exclusion.** A provider with two thousand active budgets is looking at a hundred
 * pages, and ticking them one at a time is not a thing anyone will do - so everything affected is in
 * scope and unticking a row takes it out. That also means the page only ever holds twenty rows, and
 * the request that starts the run carries the exclusions rather than the whole set.
 *
 * Active budgets only by default. The status picker is open rather than a draft yes/no, so a provider
 * can work to whatever combination their own process uses.
 */
const BudgetStep: React.FC<ComponentProps> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  const { cutover_date, pairs = [], service_type_ids = [], status_ids = [], excluded_budget_ids = [] } = value;

  const [page, setPage] = useState(1);

  // MultiSelect reads its options from the store but does not load them, so the fetch is dispatched
  // here. Without it the picker renders "No options" and the status filter looks broken.
  useEffect(() => {
    dispatch(getBudgetStatuses({ sort: ['sequence'] }));
  }, [dispatch]);

  // Query skipped: no service types reached this step, so no budget can be affected (and the server
  // would refuse the run with 'At least one service type is required.').
  const isSkipped = !cutover_date || pairs.length === 0 || service_type_ids.length === 0;

  const { data, isFetching, error } = useGetCutoverBudgetsQuery(
    {
      cutover_date,
      pairs,
      service_type_ids,
      status_ids,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    { skip: isSkipped, refetchOnMountOrArgChange: true },
  );

  // `offset` is part of the RTK Query cache key, so every page turn asks for a key the store has not
  // seen and `data` comes back undefined until the page arrives. Reading that as "no budgets" blanks
  // the table, the filter and the outcome on the interaction a provider repeats most - and takes
  // `total` to zero with them, which publishes `budgets_pending: 0` and disables Next mid-turn.
  // Holding the last response and falling back to it keeps the panel up and leaves the transition to
  // TableView's own in-place `isLoading`.
  const lastPage = useRef(data);
  if (data) lastPage.current = data;
  const budgetPage = data ?? lastPage.current;

  const rows = useMemo(() => (budgetPage?.rows ?? []).map((row) => ({ ...row, id: row.budget_id })), [budgetPage]);
  const total = budgetPage?.total ?? 0;

  // `!isFetching` and `!error` because RTK keeps the previous `data` through a refetch and through an
  // error, and a stale empty result must not enable Next and skip the apply.
  const hasSettledEmpty = budgetPage !== undefined && !isFetching && !error && total === 0;
  // Only on the default (Active) view. A provider who narrowed the statuses and found nothing keeps the
  // filter and the disabled Confirm, so they can widen it again.
  const nothingToBringIntoLine = status_ids.length === 0 && (isSkipped || hasSettledEmpty);
  // Counted across every page, not from the rows on screen: a provider on page 1 of 101 would
  // otherwise be told how many of twenty rows are stuck, which says nothing about the run.
  const blockedTotal = budgetPage?.blocked_total ?? 0;

  // Narrowing the status set can leave the reader past the end of a shorter list, and it invalidates
  // every exclusion made against the old one: `total` comes back from a query with different
  // arguments, so an id no longer in the result set would still be subtracted from it. That
  // undercounts what the run will change - and if every remaining exclusion has dropped out,
  // `inScope` clamps to zero and `disableNext` strands the provider on a step that does have work.
  //
  // Keyed on the contents, not the array: `status_ids` defaults to a fresh `[]` on every render, so
  // depending on the array itself fires this on every render and snaps the reader back to page one
  // the moment they turn to page two.
  const statusKey = status_ids.join('|');

  // Seeded with the mount value so that going back to this step does not throw away the exclusions
  // the provider already made - only an actual change of filter does.
  const appliedStatusKey = useRef(statusKey);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (appliedStatusKey.current === statusKey) return;
    appliedStatusKey.current = statusKey;

    setPage(1);
    onChangeRef.current?.('excluded_budget_ids', []);
  }, [statusKey]);

  const excluded = new Set(excluded_budget_ids);

  // Selected means "in scope". Everything is, except what has been excluded and what cannot be moved -
  // a blocked budget has no revision to move onto, so including it would only produce a failure.
  const selectedIds = rows.filter(({ id, blocked_reason }) => !excluded.has(id) && !blocked_reason).map(({ id }) => id);

  const handleSelection = (ids: string[]) => {
    const nowSelected = new Set(ids);
    const next = new Set(excluded);

    // Only this page's rows can change here, so the exclusions for every other page are left alone.
    for (const row of rows) {
      if (row.blocked_reason) continue;
      if (nowSelected.has(row.id)) next.delete(row.id);
      else next.add(row.id);
    }

    onChange?.('excluded_budget_ids', [...next]);
  };

  // Rebuilt only when the blocked count crosses zero, so the column set does not churn on every page
  // turn - the comment that used to sit on the prop said this while the call was inline and produced
  // a new array on every render.
  const hasBlocked = blockedTotal > 0;
  const columns = useMemo(() => budgetColumnsFor(hasBlocked), [hasBlocked]);

  // Blocked budgets are never ticked and never excluded, so they have to come off the count
  // separately - otherwise the step promises to bring across budgets the run will not attempt.
  const inScope = Math.max(total - blockedTotal - excluded.size, 0);

  // Published so the confirmation dialog quotes the same number, and so the wizard can refuse to
  // start a run with nothing in it.
  usePublishedValue(inScope, (next) => onChange?.('budgets_pending', next));

  // Published so the wizard can offer a plain Next, with no confirmation and no run, when there is
  // nothing to confirm.
  usePublishedValue(nothingToBringIntoLine, (next) => onChange?.('budgets_empty', next));

  const emptyMessage = `No budget holds a line for any of these services running on or past the cutover date. There is nothing left to bring into line.${
    nothingToBringIntoLine ? ' Select Next to finish the cutover.' : ''
  }`;

  const outcome = [
    `Bring ${inScope} budget${inScope === 1 ? '' : 's'} into line: each affected line ends the day before the cutover, and a line for the replacement picks up the dates it gives up.`,
    'Each budget also moves onto the price plan revision carrying the replacement, and is flagged Needs Review.',
    'Open a flagged budget, check it and save it - saving clears the flag. This is the same review the bulk copy of budgets already goes through.',
    'Every budget found is included unless you untick it. Unticking one on any page keeps it out of the run.',
    'Changing the budget status filter starts the selection again, since a different set of budgets is being looked at.',
  ];

  if (blockedTotal > 0) {
    outcome.push(
      blockedTotal === 1
        ? '1 budget is marked as unable to be moved and will be left alone. Complete the price plan step for its plan, then run the assistant again.'
        : `${blockedTotal} budgets are marked as unable to be moved and will be left alone. Complete the price plan step for their plans, then run the assistant again.`,
    );
  }

  return (
    <StepPanel
      id="cutover-budgets"
      header={`${total} budget${total === 1 ? '' : 's'} holding the service past the cutover`}
      isFetching={isFetching && !budgetPage}
      error={error}
      errorMessage={error ? getErrorMessage(error) : undefined}
      isComplete={false}
      hasRows={total > 0}
      emptyMessage={emptyMessage}
      completeMessage=""
      outcome={outcome}
      warning="Confirming to the next step will change these budgets, please ensure you have verified your selection."
      // Kept on an empty Active view: the filter widens rather than narrows, so picking Draft or
      // Inactive is how a provider whose affected budgets are not Active reaches them. Hidden only
      // when the query is skipped, where no status can find anything and picking one would turn Next
      // back into a disabled Confirm.
      filter={
        isSkipped ? undefined : (
          // Laid out like step 1's pickers - label in a fixed column on the left, control taking the
          // rest - so the two screens read the same way. MultiSelect renders its label and its control
          // as siblings, which is what lets a flex row put them side by side.
          <div className="sah-cutover-status-filter sah-cutover-filter-row">
            <MultiSelect
              type="multi-lookup"
              id="cutover-status-ids"
              name="cutover-status-ids"
              label="Budget status"
              className="sah-cutover-filter-control"
              value={status_ids}
              // MultiSelect writes through `setFieldValue`, not `onChange`, and passes its own id as
              // the field name - so the name is discarded and the value routed to the form key this
              // step actually reads.
              setFieldValue={(_name: string, next: any) => onChange?.('status_ids', next ?? [])}
              selectorOptions={selectClientBudgetStatuses}
              placeholder="Active budgets only"
            />
          </div>
        )
      }
    >
      <TableView
        id="cutover-budgets-table"
        data={rows}
        columns={columns}
        readonly
        striped
        canSelect
        selectedIds={selectedIds}
        onRowSelectionChange={handleSelection}
        isRowDisabled={(row: any) => Boolean(row?.blocked_reason)}
        showAddRow={false}
        isLoading={isFetching}
        allowPagination
        paginationType="pages"
        showCount
        currentPage={page}
        pageSize={PAGE_SIZE}
        totalCount={total}
        onPageChange={setPage}
      />
    </StepPanel>
  );
};

export default BudgetStep;
